import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { smsService } from '../../services/smsService';
import { SmsConversation } from '../../types/sms';
import { useAuth } from '../../contexts/AuthContext';
import { LeadStatus } from '../../types/lead';
import SmsInboxSidebar from '../../components/Sms/SmsInboxSidebar';
import SmsConversationPanel from '../../components/Sms/SmsConversationPanel';

function smsWsUrl(token: string): string {
  const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
  const origin = apiBase.replace(/\/api\/?$/, '').replace(/^http/, 'ws');
  return `${origin}/ws/sms?token=${encodeURIComponent(token)}`;
}

type FilterTab = 'all' | 'unread' | 'starred' | LeadStatus;

const PAGE_SIZE = 50;

const SmsInboxPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<SmsConversation[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [followUpDateFrom, setFollowUpDateFrom] = useState('');
  const [followUpDateTo, setFollowUpDateTo] = useState('');
  const [statusDropOpen, setStatusDropOpen] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusDropRef = useRef<HTMLDivElement>(null);
  const wsBatchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingWsConversationIds = useRef(new Set<string>());
  const selectedIdRef = useRef<string | null>(null);
  const ignoreLeadIdDeepLinkRef = useRef(false);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const buildListParams = useCallback(
    (before?: string) => {
      const params: Parameters<typeof smsService.getConversations>[0] = {
        limit: PAGE_SIZE,
      };
      if (before) params.before = before;
      if (filterTab === 'unread') params.unreadOnly = true;
      else if (filterTab === 'starred') params.starredOnly = true;
      else if (filterTab !== 'all') params.leadStatus = filterTab;
      if (filterTab === 'follow_up') {
        if (followUpDateFrom) params.followUpDateFrom = followUpDateFrom;
        if (followUpDateTo) params.followUpDateTo = followUpDateTo;
      }
      if (debouncedSearch.length >= 2) params.search = debouncedSearch;
      return params;
    },
    [filterTab, followUpDateFrom, followUpDateTo, debouncedSearch]
  );

  const refreshUnreadCount = useCallback(async () => {
    try {
      const count = await smsService.getUnreadConversationCount();
      setTotalUnread(count);
    } catch {
      /* silent */
    }
  }, []);

  const load = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!options?.silent) setLoading(true);
      try {
        const result = await smsService.getConversations(buildListParams());
        setConversations(result.conversations);
        setHasMore(result.hasMore);
        await refreshUnreadCount();
      } catch {
        toast.error('Failed to load SMS inbox');
      } finally {
        if (!options?.silent) setLoading(false);
      }
    },
    [buildListParams, refreshUnreadCount]
  );

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || conversations.length === 0) return;
    const last = conversations[conversations.length - 1];
    if (!last) return;

    setLoadingMore(true);
    try {
      const result = await smsService.getConversations(buildListParams(last.id));
      setConversations((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        const appended = result.conversations.filter((c) => !seen.has(c.id));
        return [...prev, ...appended];
      });
      setHasMore(result.hasMore);
    } catch {
      toast.error('Failed to load more conversations');
    } finally {
      setLoadingMore(false);
    }
  }, [buildListParams, conversations, hasMore, loadingMore]);

  const fetchAndUpsertConversation = useCallback(
    async (conversationId: string) => {
      try {
        const row = await smsService.getConversationSummary(conversationId);
        if (filterTab === 'unread' && !(row.unread_count > 0)) return;
        if (filterTab === 'starred' && !row.is_starred) return;
        if (
          filterTab !== 'all' &&
          filterTab !== 'unread' &&
          filterTab !== 'starred' &&
          row.lead_status !== filterTab
        )
          return;
        if (debouncedSearch.length >= 2) return;
        setConversations((prev) => {
          const without = prev.filter((c) => c.id !== row.id);
          return [row, ...without];
        });
      } catch {
        /* not visible to this user or deleted */
      }
    },
    [debouncedSearch, filterTab]
  );

  const flushPendingWsUpdates = useCallback(() => {
    const ids = Array.from(pendingWsConversationIds.current);
    pendingWsConversationIds.current.clear();
    void Promise.all(ids.map((id) => fetchAndUpsertConversation(id)));
  }, [fetchAndUpsertConversation]);

  const queueWsConversationRefresh = useCallback(
    (conversationId: string) => {
      pendingWsConversationIds.current.add(conversationId);
      if (wsBatchTimer.current) clearTimeout(wsBatchTimer.current);
      wsBatchTimer.current = setTimeout(flushPendingWsUpdates, 300);
    },
    [flushPendingWsUpdates]
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    ignoreLeadIdDeepLinkRef.current = false;
  }, [searchParams.get('leadId')]);

  useEffect(() => {
    if (ignoreLeadIdDeepLinkRef.current) return;
    const leadId = searchParams.get('leadId');
    if (!leadId || conversations.length === 0) return;
    const match = conversations.find((c) => c.lead_id === leadId);
    if (!match || match.id === selectedIdRef.current) return;
    setSelectedId(match.id);
    setConversations((prev) =>
      prev.map((c) => (c.id === match.id ? { ...c, unread_count: 0 } : c))
    );
  }, [conversations, searchParams]);

  const clearLeadIdFromUrl = useCallback(() => {
    if (!searchParams.get('leadId')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('leadId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!statusDropOpen) return;
    const handler = (e: MouseEvent) => {
      if (statusDropRef.current && !statusDropRef.current.contains(e.target as Node)) {
        setStatusDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [statusDropOpen]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    let stopped = false;
    const connect = () => {
      if (stopped) return;
      const ws = new WebSocket(smsWsUrl(token));
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed?.type === 'sms:message_created' && parsed?.payload?.conversationId) {
            const cid = parsed.payload.conversationId as string;
            const message = parsed.payload.message || {};
            setConversations((prev) => {
              const idx = prev.findIndex((c) => c.id === cid);
              if (idx === -1) {
                queueWsConversationRefresh(cid);
                return prev;
              }
              const cur = prev[idx]!;
              const updated: SmsConversation = {
                ...cur,
                last_message_at: message.created_at || new Date().toISOString(),
                last_message_preview: message.body || cur.last_message_preview,
                unread_count:
                  message.direction === 'inbound' && cid !== selectedIdRef.current
                    ? (cur.unread_count || 0) + 1
                    : cur.unread_count,
              };
              return [updated, ...prev.filter((c) => c.id !== cid)];
            });
            void refreshUnreadCount();
            return;
          }
          if (parsed?.type === 'sms:conversation_updated' && parsed?.payload?.conversationId) {
            queueWsConversationRefresh(String(parsed.payload.conversationId));
            void refreshUnreadCount();
          }
        } catch {
          /* ignore */
        }
      };
      ws.onclose = () => {
        if (!stopped) reconnectTimer.current = setTimeout(connect, 3000);
      };
    };
    connect();
    return () => {
      stopped = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsBatchTimer.current) clearTimeout(wsBatchTimer.current);
      wsRef.current?.close();
    };
  }, [queueWsConversationRefresh, refreshUnreadCount]);

  const handleSelect = (id: string) => {
    ignoreLeadIdDeepLinkRef.current = true;
    clearLeadIdFromUrl();
    setSelectedId(id);
    setConversations((prev) => {
      const next = prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c));
      return next;
    });
    void refreshUnreadCount();
  };

  const handleAssigned = (id: string, agentName?: string) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              assigned_agent_id: user?.id ?? c.assigned_agent_id,
              assigned_agent_name: agentName ?? user?.name ?? c.assigned_agent_name,
              status: 'assigned',
            }
          : c
      )
    );
  };

  const handleLeadUpdated = (id: string, patch: Partial<SmsConversation>) => {
    setConversations((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      if (idx === -1) return prev;
      const updated = { ...prev[idx]!, ...patch };
      const rest = prev.filter((c) => c.id !== id);
      return [updated, ...rest];
    });
  };

  const handleToggleStar = async (id: string, starred: boolean) => {
    try {
      await smsService.setConversationStarred(id, starred);
      setConversations((prev) => {
        if (filterTab === 'starred' && !starred) {
          return prev.filter((c) => c.id !== id);
        }
        return prev.map((c) => (c.id === id ? { ...c, is_starred: starred } : c));
      });
      if (filterTab === 'starred' && !starred && selectedId === id) {
        setSelectedId(null);
      }
    } catch {
      toast.error('Failed to update star');
    }
  };

  const selectedConversation = conversations.find((c) => c.id === selectedId) ?? null;

  return (
    <div className="flex h-full min-h-0 w-full bg-gradient-to-br from-sky-100/40 via-background to-indigo-100/30 dark:from-background dark:via-background dark:to-background gap-3 lg:gap-4 p-3 sm:p-4">
      <SmsInboxSidebar
        conversations={conversations}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        totalUnread={totalUnread}
        selectedId={selectedId}
        search={search}
        filterTab={filterTab}
        followUpDateFrom={followUpDateFrom}
        followUpDateTo={followUpDateTo}
        statusDropOpen={statusDropOpen}
        onSearchChange={setSearch}
        onFilterTabChange={setFilterTab}
        onFollowUpDateFromChange={setFollowUpDateFrom}
        onFollowUpDateToChange={setFollowUpDateTo}
        onStatusDropToggle={() => setStatusDropOpen((v) => !v)}
        onStatusDropClose={() => setStatusDropOpen(false)}
        onSelect={handleSelect}
        onToggleStar={handleToggleStar}
        onLoadMore={loadMore}
        statusDropRef={statusDropRef}
      />

      <div className="flex-1 min-w-0 flex">
        {selectedConversation ? (
          <SmsConversationPanel
            key={selectedConversation.id}
            conversation={selectedConversation}
            wsRef={wsRef as React.RefObject<WebSocket | null>}
            onAssigned={handleAssigned}
            onLeadUpdated={handleLeadUpdated}
            onToggleStar={handleToggleStar}
            currentUserId={user?.id}
            currentUserRole={user?.role}
            currentUserName={user?.name}
          />
        ) : (
          <div className="h-full min-h-0 w-full bg-card/80 backdrop-blur-xl border border-border shadow-lg rounded-[20px] lg:rounded-[28px] flex flex-col items-center justify-center gap-4 text-muted-foreground">
            <svg
              className="w-14 h-14 opacity-20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
              />
            </svg>
            <p className="text-sm">Select a conversation to view messages</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmsInboxPage;
